import { Parcel } from "@redotech/redo-model/outbound-labels/parcel";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { formatParcelName } from "../../setting/parcels";
import { LearningIcon } from "../learning-icon";

const ADD_PACKAGE_ID = "__add_package__";

function parcelToListItem(parcel: Parcel): RedoListItem<Parcel> {
  return {
    id: formatParcelName(parcel.name),
    type: "text",
    value: parcel,
    text: formatParcelName(parcel.name),
  };
}

export const ParcelSelectDropdown = memo(function ParcelSelectDropdown({
  disabled,
  selectedParcel,
  mixedParcels,
  parcelValues,
  onParcelChange,
  label,
  usedPackageLearning,
  onAddPackage,
}: {
  disabled: boolean;
  selectedParcel: Parcel | null;
  mixedParcels: boolean;
  parcelValues: readonly Parcel[];
  onParcelChange: (parcel: Parcel) => void;
  label?: string;
  usedPackageLearning?: boolean;
  onAddPackage?: () => void;
}) {
  const [searchString, setSearchString] = useState("");

  const onOptionSelected = ({
    item,
  }: {
    item: RedoListItem<Parcel | null>;
  }) => {
    if (item.id === ADD_PACKAGE_ID) {
      onAddPackage?.();
      return;
    }
    if (item.value) {
      onParcelChange(item.value);
    }
  };

  const searcher = useMemo(
    () =>
      new Fuse([...parcelValues], {
        keys: [
          { name: "name", getFn: (parcel) => formatParcelName(parcel.name) },
        ],
        threshold: 0.3,
      }),
    [parcelValues],
  );

  const filteredParcelValues = useMemo(() => {
    if (!searchString.trim()) {
      return parcelValues;
    }
    return searcher.search(searchString).map((result) => result.item);
  }, [parcelValues, searcher, searchString]);

  const carrierPackageText = useMemo(() => {
    if (selectedParcel && "carrier" in selectedParcel) {
      return `Using a ${selectedParcel.carrier} package will only show ${selectedParcel.carrier} rates`;
    }
    return null;
  }, [selectedParcel]);

  const options = useMemo(() => {
    const parcelOptions: RedoListItem<Parcel | null>[] =
      filteredParcelValues.map(parcelToListItem);
    if (onAddPackage) {
      parcelOptions.push({
        id: ADD_PACKAGE_ID,
        type: "text",
        value: null,
        text: "+ Add another package",
      });
    }
    return parcelOptions;
  }, [filteredParcelValues, onAddPackage]);

  // Virtualize once the list is big enough that mounting every row on open
  // becomes noticeable (merchants with thousands of parcels).
  const shouldVirtualize = parcelValues.length > 100;

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput<Parcel | null>
        disabled={disabled}
        label={
          usedPackageLearning ? (
            <Flex gap="xs">
              <Text fontSize="xs">{label}</Text>
              {usedPackageLearning && <LearningIcon />}
            </Flex>
          ) : (
            label
          )
        }
        options={options}
        optionSelected={onOptionSelected}
        placeholder={mixedParcels ? "{mixed}" : "Select parcel"}
        searchPlaceholder="Search packages..."
        searchString={searchString}
        selectedItem={
          selectedParcel && !mixedParcels
            ? parcelToListItem(selectedParcel)
            : undefined
        }
        setSearchString={setSearchString}
        size="xs"
        virtualize={shouldVirtualize}
      />
      {carrierPackageText && (
        <Text fontSize="xs" textColor="secondary">
          {carrierPackageText}
        </Text>
      )}
    </Flex>
  );
});
